import { PromotionValidity } from "../api/online-ordering-service/model/promotionValidity";

/**
 * Whether a promotion's validity window is currently open — dates AND weekday AND
 * time-of-day ranges. Mirrors the backend `business_logic/window.is_within_window`
 * (incl. overnight-aware ranges) so the OO client's eligibility matches the engine.
 *
 * Uses the customer's LOCAL clock (the client has no branch timezone); the backend
 * recomputes against the branch timezone, so this is a display-side estimate.
 *
 * Previously only startDate/endDate were checked — happy-hour ("18:00-22:00") and
 * weekend-only promos applied at any time/day (audit O-H2).
 */
const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

function minutesOfDay(hhmm: string): number {
  const [h, m] = (hhmm || "00:00").split(":").map((n) => Number(n) || 0);
  return h * 60 + m;
}

export function isPromotionWithinWindow(
  validity: PromotionValidity | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!validity) return true;

  const start = validity.startDate ? new Date(validity.startDate) : null;
  const end = validity.endDate ? new Date(validity.endDate) : null;
  if (start && now < start) return false;
  if (end && now > end) return false;

  const weekdays = validity.validWeekdays;
  if (weekdays && weekdays.length > 0) {
    const today = WEEKDAYS[now.getDay()];
    if (!weekdays.map((w) => String(w).toLowerCase()).includes(today)) return false;
  }

  const ranges = validity.validTimeRanges;
  if (ranges && ranges.length > 0) {
    const mins = now.getHours() * 60 + now.getMinutes();
    const inAnyRange = ranges.some((r) => {
      const from = minutesOfDay(r.from_time);
      const to = minutesOfDay(r.to_time);
      // Overnight range (e.g. 22:00-02:00): valid if after `from` OR before `to`.
      return from <= to ? mins >= from && mins <= to : mins >= from || mins <= to;
    });
    if (!inAnyRange) return false;
  }

  return true;
}
